import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { TimePicker } from '@/components/ui/time-picker';
import { DayCheckboxGroup } from '../../DayCheckboxGroup';
import { RecurrenceSelector, type RecurrenceType } from '../../RecurrenceSelector';
import { EndDateSelector, type EndType } from '../../EndDateSelector';
import type { StartType } from '../hooks/useAvailabilityWizard';

interface RecurrencePatternStepProps {
  // Recurrence settings
  recurrenceType: RecurrenceType;
  onRecurrenceTypeChange: (type: RecurrenceType) => void;
  recurrenceInterval: number;
  onRecurrenceIntervalChange: (interval: number) => void;
  monthlyWeek: number;
  onMonthlyWeekChange: (week: number) => void;
  monthlyDay: number;
  onMonthlyDayChange: (day: number) => void;

  // Day selection (for weekly/biweekly)
  selectedDays: number[];
  onSelectedDaysChange: (days: number[]) => void;

  // Start settings
  startType: StartType;
  onStartTypeChange: (type: StartType) => void;
  effectiveFrom: Date | null;
  onEffectiveFromChange: (date: Date | null) => void;

  // End settings
  endType: EndType;
  onEndTypeChange: (type: EndType) => void;
  endDate: Date | null;
  onEndDateChange: (date: Date | null) => void;
  occurrences: number;
  onOccurrencesChange: (count: number) => void;

  // Time
  startTime: string;
  onStartTimeChange: (time: string) => void;
  endTime: string;
  onEndTimeChange: (time: string) => void;
}

export function RecurrencePatternStep({
  recurrenceType,
  onRecurrenceTypeChange,
  recurrenceInterval,
  onRecurrenceIntervalChange,
  monthlyWeek,
  onMonthlyWeekChange,
  monthlyDay,
  onMonthlyDayChange,
  selectedDays,
  onSelectedDaysChange,
  startType,
  onStartTypeChange,
  effectiveFrom,
  onEffectiveFromChange,
  endType,
  onEndTypeChange,
  endDate,
  onEndDateChange,
  occurrences,
  onOccurrencesChange,
  startTime,
  onStartTimeChange,
  endTime,
  onEndTimeChange,
}: RecurrencePatternStepProps) {
  const { t } = useTranslation('staff');
  const [startCalendarOpen, setStartCalendarOpen] = useState(false);

  const timeError = startTime >= endTime;

  return (
    <div className="p-6 space-y-6">
      {/* Recurrence type selector */}
      <div className="space-y-3">
        <Label variant="section">
          {t('availability.recurrence.repeats', 'Repeats')}
        </Label>
        <RecurrenceSelector
          recurrenceType={recurrenceType}
          onRecurrenceTypeChange={onRecurrenceTypeChange}
          recurrenceInterval={recurrenceInterval}
          onRecurrenceIntervalChange={onRecurrenceIntervalChange}
          monthlyWeek={monthlyWeek}
          onMonthlyWeekChange={onMonthlyWeekChange}
          monthlyDay={monthlyDay}
          onMonthlyDayChange={onMonthlyDayChange}
        />
      </div>

      {/* Day selection (only for weekly/biweekly) */}
      {recurrenceType !== 'monthly' && (
        <div className="space-y-3">
          <Label variant="section">
            {t('availability.dialog.days', 'Days')}
          </Label>
          <DayCheckboxGroup
            value={selectedDays}
            onChange={onSelectedDaysChange}
            showPresets={false}
            showSummary={false}
          />
        </div>
      )}

      {/* Time selection */}
      <div className="space-y-3">
        <Label variant="section">
          {t('availability.wizard.steps.time', 'Time')}
        </Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>
              {t('availability.dialog.startTime', 'Start Time')}
            </Label>
            <TimePicker
              value={startTime}
              onChange={onStartTimeChange}
              placeholder={t('availability.dialog.selectStartTime', 'Select start time')}
            />
          </div>
          <div className="space-y-2">
            <Label>
              {t('availability.dialog.endTime', 'End Time')}
            </Label>
            <TimePicker
              value={endTime}
              onChange={onEndTimeChange}
              placeholder={t('availability.dialog.selectEndTime', 'Select end time')}
            />
          </div>
        </div>
        {timeError && (
          <p className="text-sm text-red-600">
            {t('availability.errors.endTimeAfterStart', 'End time must be after start time')}
          </p>
        )}
      </div>

      {/* Starts / Ends — side by side on desktop */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Start settings */}
        <div className="space-y-3">
          <Label variant="section">
            {t('availability.starts.label', 'Starts')}
          </Label>
          <RadioGroup
            value={startType}
            onValueChange={(v) => onStartTypeChange(v as StartType)}
            className="space-y-3"
          >
            <div className="flex items-center space-x-3">
              <RadioGroupItem value="immediately" id="start-immediately" />
              <Label htmlFor="start-immediately" className="font-normal cursor-pointer">
                {t('availability.starts.immediately', 'Immediately')}
              </Label>
            </div>
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="on_date" id="start-on-date" className="mt-2" />
              <div className="flex-1 space-y-2">
                <Label htmlFor="start-on-date" className="font-normal cursor-pointer">
                  {t('availability.starts.onDate', 'On date')}
                </Label>
                {startType === 'on_date' && (
                  <Popover open={startCalendarOpen} onOpenChange={setStartCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !effectiveFrom && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {effectiveFrom ? format(effectiveFrom, 'PPP') : t('availability.dialog.pickStartDate', 'Pick a start date')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={effectiveFrom || undefined}
                        onSelect={(date) => {
                          onEffectiveFromChange(date || null);
                          setStartCalendarOpen(false);
                        }}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>
          </RadioGroup>
        </div>

        {/* End settings */}
        <div className="space-y-3">
          <Label variant="section">
            {t('availability.ends.label', 'Ends')}
          </Label>
          <EndDateSelector
            endType={endType}
            onEndTypeChange={onEndTypeChange}
            endDate={endDate}
            onEndDateChange={onEndDateChange}
            occurrences={occurrences}
            onOccurrencesChange={onOccurrencesChange}
          />
        </div>
      </div>
    </div>
  );
}
